import type { StoreApi, UseBoundStore } from 'zustand';
import {
  createDataSourceControllerSliceKeysSelectors,
  createDataSourceControllerSliceSelectors,
} from '../../slices/DataSourceControllerSlice/selectors';
import type { GlobalStateStoreType } from './types';

export const createGlobalStateStoreSelectors = (
  store: UseBoundStore<StoreApi<GlobalStateStoreType>>,
) => {
  const { dataSourceKeysSelectors } = createDataSourceControllerSliceKeysSelectors(store);

  const storeSelectors = {
    usePageInputs: () => store((state) => state.pageInputs),
    usePageParams: () => store((state) => state.pageParams),
    useBlocks: () => store((state) => state.blocks),
    useBlockRefs: () => store((state) => state.blockRefs),
    usePageVariables: () => store((state) => state.pageVariables),
    usePageFunctions: () => store((state) => state.pageFunctions),
    useActions: () => store.getState().actions,
    useContextualDialogsForBlocks: () => store((state) => state.contextualDialogForBlocks),
    useBlockLifecycles: () => store((state) => state.blockLifecycles),
    useMediaUploads: () => store((state) => state.mediaUploads),
    useBlockSelection: () => store((state) => state.blockSelection),
    useDraggableData: () => store((state) => state.draggableData),
    useDraggableBlockIds: () => store((state) => state.draggableBlockIds),
    useOnOutputChange: () => store((state) => state.onOutputChange),
    useBlockSelectionActive: () => store((state) => state.blockSelectionActive),
    ...dataSourceKeysSelectors,
  };

  const { dataSourceSelectors } = createDataSourceControllerSliceSelectors(store);

  const blockSelectors = {
    useDetails: (blockId?: string) => {
      return store((state) => (blockId ? state.blocks[blockId] : undefined));
    },
    useRef: (blockId: string) => {
      return store((state) => state.blockRefs[blockId]);
    },
    useContextualDialog: (blockId: string) => {
      return store((state) => state.contextualDialogForBlocks[blockId]);
    },
    useLifecycle: (blockId: string) => {
      return store((state) => state.blockLifecycles[blockId]);
    },
    useMediaUploads: (blockId: string) => {
      return store((state) => state.mediaUploads[blockId]);
    },
  };

  const flowsSelectors = {
    useImportObjectRecords: () => store((state) => state.flows.importObjectRecords),
    useShareEntityRecord: () => store((state) => state.flows.shareEntityRecord),
    useControlMediaViewer: () => store((state) => state.flows.controlMediaViewer),
    useBlockSelector: () => store((state) => state.flows.blockSelector),
  };

  const pageFunctionSelectors = {
    useResult: (pageFunctionId: string) => {
      return store((state) => state.pageFunctions[pageFunctionId]);
    },
  };

  const pageVariableSelectors = {
    useResult: (pageVariableId: string) => {
      return store((state) => state.pageVariables[pageVariableId]);
    },
  };

  return Object.assign(store, {
    hooks: {
      ...storeSelectors,
      dataSource: dataSourceSelectors,
      pageVariable: pageVariableSelectors,
      block: blockSelectors,
      flows: flowsSelectors,
      pageFunction: pageFunctionSelectors,
    },
  });
};

export type GlobalStateStoreInstance = ReturnType<typeof createGlobalStateStoreSelectors>;
